<template>
  <!-- Compact inline badge within header -->
  <div v-if="isActing && actingContext" class="acting-badge">
    <div class="acting-badge__pill">
      <img
        v-if="actingContext.managedUserAvatar"
        :src="actingContext.managedUserAvatar"
        :alt="actingContext.managedUserName"
        class="acting-badge__avatar"
      />
      <v-icon v-else size="14" class="acting-badge__icon"
        >mdi-account-switch</v-icon
      >
      <span class="acting-badge__text">
        Acting as <strong>{{ actingContext.managedUserName }}</strong>
      </span>
      <span class="acting-badge__timer">{{ remainingTime }}</span>
      <button
        class="acting-badge__exit"
        @click="exitActingMode"
        title="Exit Acting Mode"
      >
        <v-icon size="14">mdi-close</v-icon>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted } from "vue";
import { useActingContext } from "@/composables/useActingContext";

const {
  actingContext,
  isActing,
  remainingTime,
  exitActingMode,
  startTimer,
  stopTimer,
} = useActingContext();

onMounted(() => {
  startTimer();
});

onUnmounted(() => {
  stopTimer();
});
</script>

<style scoped>
.acting-badge {
  display: inline-flex;
  align-items: center;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.acting-badge__pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 6px 10px;
  border-radius: 16px;
  font-size: 13px;
  line-height: 1.4;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  transition: all 0.2s ease;
}

.acting-badge__pill:hover {
  box-shadow: 0 3px 12px rgba(102, 126, 234, 0.4);
  transform: translateY(-1px);
}

.acting-badge__avatar {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.acting-badge__icon {
  flex-shrink: 0;
  opacity: 0.95;
}

.acting-badge__text {
  white-space: nowrap;
  font-size: 12px;
}

.acting-badge__text strong {
  font-weight: 600;
}

.acting-badge__timer {
  font-size: 11px;
  opacity: 0.85;
  padding: 2px 6px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  white-space: nowrap;
}

.acting-badge__exit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  margin-left: 2px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.acting-badge__exit:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.acting-badge__exit:active {
  transform: scale(0.95);
}

/* Responsive - hide text on very small screens */
@media (max-width: 480px) {
  .acting-badge__text {
    display: none;
  }

  .acting-badge__pill {
    gap: 4px;
    padding: 6px 8px;
  }
}
</style>
